import Icon from '@/components/ui/AppIcon';

interface Commitment {
  title: string;
  description: string;
  timeframe: string;
  icon: string;
  priority: 'high' | 'medium' | 'standard';
}

const ResponseCommitments = () => {
  const commitments: Commitment[] = [
    {
      title: 'Enterprise Inquiries',
      description: 'Accounts exceeding $100,000 monthly spend receive executive-level response',
      timeframe: '6 business hours',
      icon: 'BoltIcon',
      priority: 'high'
    },
    {
      title: 'Technical Integration',
      description: 'Platform access, API documentation, and white-label specification requests',
      timeframe: '12 business hours',
      icon: 'CodeBracketIcon',
      priority: 'high'
    },
    {
      title: 'Compliance & Legal',
      description: 'Regulatory framework questions, jurisdiction inquiries, risk assessments',
      timeframe: '24 business hours',
      icon: 'ShieldCheckIcon',
      priority: 'medium'
    },
    {
      title: 'General Inquiries',
      description: 'Service overview, pricing structure, partnership evaluation requests',
      timeframe: '24 business hours',
      icon: 'InformationCircleIcon',
      priority: 'standard'
    }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-warning';
      default:
        return 'text-accent';
    }
  };

  const getPriorityBg = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-error bg-opacity-10 border-error';
      case 'medium':
        return 'bg-warning bg-opacity-10 border-warning';
      default:
        return 'bg-accent bg-opacity-10 border-accent';
    }
  };

  return (
    <section className="bg-card border border-border rounded-sm p-8">
      <div className="flex items-center space-x-3 mb-8">
        <Icon name="ClockIcon" size={28} variant="solid" className="text-primary" />
        <h2 className="font-headline font-bold text-2xl text-primary">
          Response Time Commitments
        </h2>
      </div>
      
      <p className="font-body text-base text-text-secondary mb-8 leading-relaxed">
        Transparent service level agreements for all inquiry types. Response times measured in business hours (Monday-Friday, 9:00-18:00 HKT).
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {commitments.map((commitment, index) => (
          <div 
            key={index} 
            className={`border-2 rounded-sm p-6 ${getPriorityBg(commitment.priority)}`}
          >
            <div className="flex items-start justify-between mb-4">
              <Icon 
                name={commitment.icon as any} 
                size={32} 
                variant="solid" 
                className={getPriorityColor(commitment.priority)}
              />
              <div className="text-right">
                <div className={`font-accent text-xs uppercase tracking-wider mb-1 ${getPriorityColor(commitment.priority)}`}>
                  {commitment.priority} Priority
                </div>
                <div className={`font-headline font-bold text-lg ${getPriorityColor(commitment.priority)}`}>
                  {commitment.timeframe}
                </div>
              </div>
            </div>
            
            <h3 className="font-headline font-bold text-base text-primary mb-2">
              {commitment.title}
            </h3>
            
            <p className="font-body text-sm text-text-secondary leading-relaxed">
              {commitment.description}
            </p>
          </div>
        ))}
      </div>
      
      <div className="space-y-4">
        <div className="p-6 bg-muted rounded-sm">
          <div className="flex items-start space-x-3">
            <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-success mt-1" />
            <div>
              <h4 className="font-headline font-semibold text-sm text-primary mb-2">
                Guaranteed Response Protocol
              </h4>
              <p className="font-body text-sm text-text-secondary leading-relaxed">
                All inquiries receive acknowledgment within 2 business hours. Full technical or compliance responses delivered within committed timeframes. If we cannot meet the commitment, we notify you immediately with revised timeline.
              </p>
            </div>
          </div>
        </div>
        
        <div className="p-6 bg-surface border border-border rounded-sm">
          <div className="flex items-start space-x-3">
            <Icon name="ExclamationTriangleIcon" size={24} variant="solid" className="text-warning mt-1" />
            <div>
              <h4 className="font-headline font-semibold text-sm text-primary mb-2">
                What We Don&apos;t Do
              </h4>
              <p className="font-body text-sm text-text-secondary leading-relaxed">
                No automated follow-ups, no sales pressure, no performance guarantees. We respond once with complete information. If you need clarification, we&apos;re available. Otherwise, the decision is yours without further contact.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResponseCommitments;